import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Assignment,
  Policy as PolicyIcon,
  Water as WaterIcon,
  Agriculture as Farm,
  ExpandMore,
  Visibility,
  AttachMoney,
  Warning,
  Info,
  Description,
  Close,
} from '@mui/icons-material';
import { useAuth } from 'hooks/useAuth';
import { claimsAPI, policiesAPI, pondsAPI, farmsAPI } from 'services/api';
import { useNavigate } from 'react-router-dom';
import DOCProgressIndicator from 'shared/components/DOCProgressIndicator';

const ClaimsManagement = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [claims, setClaims] = useState([]);
  const [policies, setPolicies] = useState([]);
  const [ponds, setPonds] = useState([]);
  const [farms, setFarms] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingClaim, setEditingClaim] = useState(null);
  const [openDetailsDialog, setOpenDetailsDialog] = useState(false);
  const [viewingClaim, setViewingClaim] = useState(null);
  const [viewingClaimPolicy, setViewingClaimPolicy] = useState(null);
  const [formData, setFormData] = useState({
    policy_id: '',
    claim_type: 'disease',
    incident_date: '',
    description: '',
    estimated_loss: '',
    affected_area: '',
    cause_of_loss: '',
    supporting_documents: '',
  });

  useEffect(() => {
    if (user) {
      loadData();
    }
  }, [user]);

  const loadData = async () => {
    if (!user) {
      console.warn('User not available, skipping data load');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const [claimsResponse, policiesResponse, farmsResponse] = await Promise.all([
        claimsAPI.getAll(),
        policiesAPI.getAll(),
        farmsAPI.getAll()
      ]);

      // Filter data for current user
      const userFarms = farmsResponse.data.filter(farm => farm.user_id === user.id);
      const userPonds = await pondsAPI.getAllForUser();
      const userPolicies = policiesResponse.data.filter(policy => 
        userPonds.some(pond => pond.id === policy.pond_id)
      );
      const userClaims = claimsResponse.data.filter(claim => 
        userPolicies.some(policy => policy.id === claim.policy_id)
      );

      setClaims(userClaims);
      setPolicies(userPolicies);
      setPonds(userPonds);
      setFarms(userFarms);
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Failed to load data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (claim = null) => {
    if (claim) {
      setEditingClaim(claim);
      setFormData({
        policy_id: claim.policy_id || '',
        claim_type: claim.claim_type || 'disease',
        incident_date: claim.incident_date ? claim.incident_date.split('T')[0] : '',
        description: claim.description || '',
        estimated_loss: claim.estimated_loss || '',
        affected_area: claim.affected_area || '',
        cause_of_loss: claim.cause_of_loss || '',
        supporting_documents: claim.supporting_documents || '',
      });
    } else {
      setEditingClaim(null);
      setFormData({
        policy_id: '',
        claim_type: 'disease',
        incident_date: '',
        description: '',
        estimated_loss: '',
        affected_area: '',
        cause_of_loss: '',
        supporting_documents: '',
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingClaim(null);
    setFormData({
      policy_id: '',
      claim_type: 'disease',
      incident_date: '',
      description: '',
      estimated_loss: '',
      affected_area: '',
      cause_of_loss: '',
      supporting_documents: '',
    });
  };

  const validateForm = () => {
    const errors = {};

    // Required field validations
    if (!formData.policy_id) {
      errors.policy_id = 'Please select a policy';
    }

    if (!formData.incident_date) {
      errors.incident_date = 'Incident date is required';
    }

    if (!formData.description?.trim()) {
      errors.description = 'Description is required';
    }

    if (!formData.cause_of_loss?.trim()) {
      errors.cause_of_loss = 'Cause of loss is required';
    }

    // Numeric validations
    if (!formData.estimated_loss || parseFloat(formData.estimated_loss) <= 0) {
      errors.estimated_loss = 'Valid estimated loss amount is required';
    }

    if (!formData.affected_area || parseFloat(formData.affected_area) <= 0) {
      errors.affected_area = 'Valid affected area is required';
    }

    // Business rule validations
    if (formData.estimated_loss && parseFloat(formData.estimated_loss) > 1000000) {
      errors.estimated_loss = 'Estimated loss seems unusually high. Please verify.';
    }

    if (formData.affected_area && parseFloat(formData.affected_area) > 100) {
      errors.affected_area = 'Affected area cannot exceed 100%';
    }

    // Date validation - incident should not be in future
    if (formData.incident_date && new Date(formData.incident_date) > new Date()) {
      errors.incident_date = 'Incident date cannot be in the future';
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    console.log('Claim form submission started:', { editingClaim: !!editingClaim, formData });
    
    setError(null);
    setFieldErrors({});

    if (!validateForm()) {
      console.log('Claim form validation failed');
      return;
    }

    console.log('Claim validation passed, attempting API call...');

    try {
      const claimData = {
        ...formData,
        policy_id: parseInt(formData.policy_id),
        estimated_loss: parseFloat(formData.estimated_loss || 0),
        affected_area: parseFloat(formData.affected_area || 0),
        user_id: user.id,
      };

      console.log('Claim data to be sent:', claimData);

      if (editingClaim) {
        console.log('Updating claim:', editingClaim.id);
        await claimsAPI.update(editingClaim.id, claimData);
        console.log('Claim update successful');
      } else {
        console.log('Creating new claim');
        await claimsAPI.create(claimData);
        console.log('Claim creation successful');
      }

      handleCloseDialog();
      loadData();
    } catch (err) {
      console.error('Error saving claim:', err);
      
      if (err.response && err.response.status === 422) {
        const errorData = err.response.data;
        if (errorData.detail) {
          if (typeof errorData.detail === 'string') {
            const errorDetail = errorData.detail;
            if (errorDetail.includes('Loss percentage')) setFieldErrors({ estimated_loss: errorDetail });
            else if (errorDetail.includes('policy')) setFieldErrors({ policy_id: errorDetail });
            else if (errorDetail.includes('incident')) setFieldErrors({ incident_date: errorDetail });
            else if (errorDetail.includes('description')) setFieldErrors({ description: errorDetail });
            else setError(errorDetail);
          } else if (Array.isArray(errorData.detail)) {
            const fieldMap = {};
            errorData.detail.forEach(error => {
              if (error.loc && error.loc.length > 0) {
                const fieldName = error.loc[error.loc.length - 1];
                fieldMap[fieldName] = error.msg || 'Invalid value';
              }
            });
            setFieldErrors(fieldMap);
          } else {
            setError('Validation error occurred. Please check your data.');
          }
        } else {
          setError('Validation error occurred. Please check your data.');
        }
      } else if (err.response && err.response.status === 404) {
        setError('Claim not found. Please refresh and try again.');
      } else if (err.response && err.response.status === 403) {
        setError('Access denied. You can only edit your own claims.');
      } else {
        setError('Failed to save claim. Please check your data and try again.');
      }
    }
  };

  const handleDelete = async (claimId) => {
    if (window.confirm('Are you sure you want to delete this claim?')) {
      try {
        await claimsAPI.delete(claimId);
        loadData();
      } catch (err) {
        console.error('Error deleting claim:', err);
        setError('Failed to delete claim. Please try again.');
      }
    }
  };

  const handleViewDetails = async (claim) => {
    setViewingClaim(claim);
    setOpenDetailsDialog(true);
    
    // Load policy data for DOC calculation
    if (claim.policy_id) {
      try {
        const policyResponse = await policiesAPI.getById(claim.policy_id);
        setViewingClaimPolicy(policyResponse.data);
      } catch (err) {
        console.error('Error loading policy for claim:', err);
      }
    }
  };

  const handleCloseDetailsDialog = () => {
    setOpenDetailsDialog(false);
    setViewingClaim(null);
    setViewingClaimPolicy(null);
  };

  const getStatusColor = (status) => {
    if (!status) return 'default';
    switch (status) {
      case 'approved': return 'success';
      case 'pending': return 'warning';
      case 'rejected': return 'error';
      case 'under_review': return 'info';
      default: return 'default';
    }
  };

  const getClaimTypeColor = (type) => {
    if (!type) return 'default';
    switch (type) {
      case 'disease': return 'error';
      case 'natural_disaster': return 'warning';
      case 'equipment_failure': return 'info';
      case 'water_quality': return 'secondary';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => handleOpenDialog()}
        >
          Submit New Claim
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="h6">
                    Total Claims
                  </Typography>
                  <Typography variant="h4" component="div" color="primary">
                    {claims.length}
                  </Typography>
                </Box>
                <Assignment fontSize="large" color="primary" />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="h6">
                    Pending Claims
                  </Typography>
                  <Typography variant="h4" component="div" color="warning.main">
                    {claims.filter(c => c.status === 'pending').length}
                  </Typography>
                </Box>
                <Warning fontSize="large" color="warning" />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="h6">
                    Approved Claims
                  </Typography>
                  <Typography variant="h4" component="div" color="success.main">
                    {claims.filter(c => c.status === 'approved').length}
                  </Typography>
                </Box>
                <Assignment fontSize="large" color="success" />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="h6">
                    Total Claimed
                  </Typography>
                  <Typography variant="h4" component="div" color="info.main">
                    ₹{claims.reduce((sum, claim) => sum + (claim.estimated_loss || 0), 0).toFixed(2)}
                  </Typography>
                </Box>
                <AttachMoney fontSize="large" color="info" />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Claims List */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Your Insurance Claims
          </Typography>
          {claims.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Assignment sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="textSecondary" gutterBottom>
                No claims submitted yet
              </Typography>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Submit your first claim to get compensation for your losses
              </Typography>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => handleOpenDialog()}
                sx={{ mt: 2 }}
              >
                Submit Your First Claim
              </Button>
            </Box>
          ) : (
            <Box>
              {claims.map((claim) => {
                const policy = policies.find(p => p.id === claim.policy_id);
                const pond = ponds.find(po => po.id === policy?.pond_id);
                const farm = farms.find(f => f.id === pond?.farm_id);
                
                return (
                  <Accordion key={claim.id} sx={{ mb: 2 }}>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                        <Assignment sx={{ mr: 2, color: 'primary.main' }} />
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography variant="h6">
                            Claim #{claim.id} - {(claim.claim_type || 'unknown').replace('_', ' ').toUpperCase()}
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            {farm?.name} • {pond?.name} • Policy #{policy?.id}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1, mr: 2 }}>
                          <Chip
                            label={claim.status || 'unknown'}
                            color={getStatusColor(claim.status)}
                            size="small"
                          />
                          <Chip
                            label={(claim.claim_type || 'unknown').replace('_', ' ')}
                            color={getClaimTypeColor(claim.claim_type)}
                            size="small"
                          />
                        </Box>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <Typography variant="subtitle2" gutterBottom>
                            Claim Details
                          </Typography>
                          <Typography variant="body2">
                            <strong>Claim Type:</strong> {(claim.claim_type || 'unknown').replace('_', ' ').toUpperCase()}
                          </Typography>
                          <Typography variant="body2">
                            <strong>Incident Date:</strong> {claim.incident_date ? new Date(claim.incident_date).toLocaleDateString() : 'Not specified'}
                          </Typography>
                          <Typography variant="body2">
                            <strong>Estimated Loss:</strong> ₹{claim.estimated_loss ? parseFloat(claim.estimated_loss).toFixed(2) : '0.00'}
                          </Typography>
                          <Typography variant="body2">
                            <strong>Affected Area:</strong> {claim.affected_area ? parseFloat(claim.affected_area).toFixed(2) : '0.00'} hectares
                          </Typography>
                          <Typography variant="body2">
                            <strong>Cause of Loss:</strong> {claim.cause_of_loss || 'Not specified'}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Typography variant="subtitle2" gutterBottom>
                            Policy & Farm Information
                          </Typography>
                          <Typography variant="body2">
                            <strong>Policy:</strong> #{policy?.id || 'N/A'} - {policy?.species || 'Unknown'}
                          </Typography>
                          <Typography variant="body2">
                            <strong>Farm:</strong> {farm?.name || 'Unknown Farm'}
                          </Typography>
                          <Typography variant="body2">
                            <strong>Pond:</strong> {pond?.name || 'Unknown Pond'}
                          </Typography>
                          <Typography variant="body2">
                            <strong>Submitted:</strong> {claim.created_at ? new Date(claim.created_at).toLocaleDateString() : 'Not specified'}
                          </Typography>
                          {claim.approved_amount && (
                            <Typography variant="body2">
                              <strong>Approved Amount:</strong> ₹{claim.approved_amount ? parseFloat(claim.approved_amount).toFixed(2) : '0.00'}
                            </Typography>
                          )}
                        </Grid>
                        {claim.description && (
                          <Grid item xs={12}>
                            <Typography variant="subtitle2" gutterBottom>
                              Description
                            </Typography>
                            <Typography variant="body2">
                              {claim.description}
                            </Typography>
                          </Grid>
                        )}
                        {claim.supporting_documents && (
                          <Grid item xs={12}>
                            <Typography variant="subtitle2" gutterBottom>
                              Supporting Documents
                            </Typography>
                            <Typography variant="body2">
                              {claim.supporting_documents}
                            </Typography>
                          </Grid>
                        )}
                        <Grid item xs={12}>
                          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                            <Button
                              size="small"
                              startIcon={<Visibility />}
                              onClick={() => handleViewDetails(claim)}
                            >
                              View Details
                            </Button>
                            {claim.status === 'pending' && (
                              <Button
                                size="small"
                                startIcon={<Edit />}
                                onClick={() => handleOpenDialog(claim)}
                              >
                                Edit
                              </Button>
                            )}
                            {claim.status === 'pending' && (
                              <Button
                                size="small"
                                startIcon={<Delete />}
                                onClick={() => handleDelete(claim.id)}
                                color="error"
                              >
                                Delete
                              </Button>
                            )}
                          </Box>
                        </Grid>
                      </Grid>
                    </AccordionDetails>
                  </Accordion>
                );
              })}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Professional Add/Edit Claim Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog} 
        maxWidth="lg" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 24px 48px rgba(0, 0, 0, 0.15)',
            overflow: 'hidden'
          }
        }}
      >
        {/* Enhanced Header */}
        <DialogTitle sx={{ 
          pb: 0, 
          background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
          color: 'white',
          position: 'relative',
          overflow: 'hidden'
        }}>
          <Box sx={{ position: 'relative', zIndex: 2 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box display="flex" alignItems="center">
                <Box sx={{ 
                  p: 1.5, 
                  borderRadius: 2, 
                  bgcolor: 'rgba(255, 255, 255, 0.15)',
                  mr: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Assignment sx={{ fontSize: 28 }} />
                </Box>
                <Box>
                  <Typography variant="h5" component="div" sx={{ fontWeight: 600, mb: 0.5 }}>
                    {editingClaim ? 'Edit Claim' : 'Submit New Claim'}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    {editingClaim ? 'Update claim information and details' : 'Report a new insurance claim for your pond'}
                  </Typography>
                </Box>
              </Box>
              <IconButton 
                onClick={handleCloseDialog}
                sx={{ 
                  color: 'white',
                  bgcolor: 'rgba(255, 255, 255, 0.1)',
                  '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.2)' }
                }}
              >
                <Close />
              </IconButton>
            </Box>
          </Box>
          {/* Decorative background pattern */}
          <Box sx={{
            position: 'absolute',
            top: -50,
            right: -50,
            width: 200,
            height: 200,
            borderRadius: '50%',
            background: 'rgba(255, 255, 255, 0.05)',
            zIndex: 1
          }} />
        </DialogTitle>

        <form onSubmit={handleSubmit}>
          <DialogContent sx={{ p: 0, bgcolor: '#fafafa' }}>
            <Box sx={{ p: 4 }}>
              {/* Policy Selection Card */}
              <Card sx={{ 
                mb: 4, 
                background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
                border: '1px solid #e3f2fd',
                borderRadius: 3,
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)'
              }}>
                <CardContent sx={{ p: 3 }}>
                  <Box display="flex" alignItems="center" mb={3}>
                    <Box sx={{ 
                      p: 1, 
                      borderRadius: 2, 
                      bgcolor: 'primary.main',
                      mr: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <PolicyIcon sx={{ color: 'white', fontSize: 20 }} />
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
                      Policy Information
                    </Typography>
                  </Box>
                  
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth required error={!!fieldErrors.policy_id}>
                        <InputLabel>Select Policy</InputLabel>
                        <Select
                          value={formData.policy_id}
                          onChange={(e) => setFormData({ ...formData, policy_id: e.target.value })}
                          label="Select Policy"
                        >
                          {policies.filter(p => p.status === 'active').map((policy) => {
                            const pond = ponds.find(po => po.id === policy.pond_id);
                            const farm = farms.find(f => f.id === pond?.farm_id);
                            return (
                              <MenuItem key={policy.id} value={policy.id}>
                                <Box>
                                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                    Policy #{policy.id}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {pond?.pond_name} - {farm?.name}
                                  </Typography>
                                </Box>
                              </MenuItem>
                            );
                          })}
                        </Select>
                        {fieldErrors.policy_id && (
                          <Typography variant="caption" color="error" sx={{ mt: 0.5, fontSize: '0.75rem' }}>
                            {String(fieldErrors.policy_id)}
                          </Typography>
                        )}
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth required>
                        <InputLabel>Claim Type</InputLabel>
                        <Select
                          value={formData.claim_type}
                          onChange={(e) => setFormData({ ...formData, claim_type: e.target.value })}
                          label="Claim Type"
                        >
                          <MenuItem value="disease">Disease Outbreak</MenuItem>
                          <MenuItem value="natural_disaster">Natural Disaster</MenuItem>
                          <MenuItem value="equipment_failure">Equipment Failure</MenuItem>
                          <MenuItem value="water_quality">Water Quality Issues</MenuItem>
                          <MenuItem value="predation">Predation</MenuItem>
                          <MenuItem value="other">Other</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Incident Details Card */}
              <Card sx={{ 
                mb: 4, 
                background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
                border: '1px solid #e3f2fd',
                borderRadius: 3,
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)'
              }}>
                <CardContent sx={{ p: 3 }}>
                  <Box display="flex" alignItems="center" mb={3}>
                    <Box sx={{ 
                      p: 1, 
                      borderRadius: 2, 
                      bgcolor: 'warning.main',
                      mr: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <Warning sx={{ color: 'white', fontSize: 20 }} />
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: 'warning.main' }}>
                      Incident Details
                    </Typography>
                  </Box>
                  
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Incident Date"
                        type="date"
                        value={formData.incident_date}
                        onChange={(e) => setFormData({ ...formData, incident_date: e.target.value })}
                        InputLabelProps={{ shrink: true }}
                        required
                        error={!!fieldErrors.incident_date}
                        helperText={fieldErrors.incident_date ? String(fieldErrors.incident_date) : ""}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Cause of Loss"
                        value={formData.cause_of_loss}
                        onChange={(e) => setFormData({ ...formData, cause_of_loss: e.target.value })}
                        placeholder="e.g., Bacterial infection, Storm damage, Equipment malfunction"
                        required
                        error={!!fieldErrors.cause_of_loss}
                        helperText={fieldErrors.cause_of_loss ? String(fieldErrors.cause_of_loss) : ""}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Estimated Loss (₹)"
                        type="number"
                        value={formData.estimated_loss}
                        onChange={(e) => setFormData({ ...formData, estimated_loss: e.target.value })}
                        placeholder="Enter estimated financial loss"
                        required
                        error={!!fieldErrors.estimated_loss}
                        helperText={fieldErrors.estimated_loss ? String(fieldErrors.estimated_loss) : ""}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Affected Area (acres)"
                        type="number"
                        step="0.1"
                        value={formData.affected_area}
                        onChange={(e) => setFormData({ ...formData, affected_area: e.target.value })}
                        placeholder="Area affected by the incident"
                        required
                        error={!!fieldErrors.affected_area}
                        helperText={fieldErrors.affected_area ? String(fieldErrors.affected_area) : ""}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Description & Documents Card */}
              <Card sx={{ 
                background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
                border: '1px solid #e3f2fd',
                borderRadius: 3,
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)'
              }}>
                <CardContent sx={{ p: 3 }}>
                  <Box display="flex" alignItems="center" mb={3}>
                    <Box sx={{ 
                      p: 1, 
                      borderRadius: 2, 
                      bgcolor: 'info.main',
                      mr: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <Description sx={{ color: 'white', fontSize: 20 }} />
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: 'info.main' }}>
                      Additional Information
                    </Typography>
                  </Box>
                  
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Detailed Description"
                        multiline
                        rows={4}
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        placeholder="Provide detailed description of the incident, including symptoms, timeline, and any immediate actions taken..."
                        required
                        error={!!fieldErrors.description}
                        helperText={fieldErrors.description ? String(fieldErrors.description) : ""}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Supporting Documents"
                        multiline
                        rows={2}
                        value={formData.supporting_documents}
                        onChange={(e) => setFormData({ ...formData, supporting_documents: e.target.value })}
                        placeholder="List any supporting documents (photos, lab reports, water quality tests, etc.)"
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Box>
          </DialogContent>

          {/* Enhanced Footer */}
          <DialogActions sx={{ 
            px: 4, 
            py: 3, 
            bgcolor: '#f8f9fa',
            borderTop: '1px solid #e3f2fd'
          }}>
            <Box sx={{ display: 'flex', gap: 2, width: '100%', justifyContent: 'flex-end' }}>
              <Button 
                onClick={handleCloseDialog}
                variant="outlined"
                sx={{ 
                  minWidth: 120,
                  px: 3,
                  py: 1.5,
                  borderRadius: 2,
                  fontWeight: 600,
                  borderColor: 'grey.300',
                  color: 'grey.700',
                  '&:hover': {
                    borderColor: 'grey.400',
                    bgcolor: 'grey.50'
                  }
                }}
              >
                Cancel
              </Button>
              <Button 
                type="submit"
                variant="contained"
                color="primary"
                sx={{ 
                  minWidth: 160,
                  px: 3,
                  py: 1.5,
                  borderRadius: 2,
                  fontWeight: 600,
                  background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                  boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
                    boxShadow: '0 6px 16px rgba(25, 118, 210, 0.4)'
                  }
                }}
              >
                {editingClaim ? 'Update Claim' : 'Submit Claim'}
              </Button>
            </Box>
          </DialogActions>
        </form>
      </Dialog>

      {/* Claim Details Dialog */}
      <Dialog 
        open={openDetailsDialog} 
        onClose={handleCloseDetailsDialog} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle sx={{ pb: 1, bgcolor: 'primary.main', color: 'white' }}>
          <Box display="flex" alignItems="center">
            <Assignment sx={{ mr: 2 }} />
            <Typography variant="h6" component="div">
              Claim Details
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          {viewingClaim && (
            <Grid container spacing={3}>
              {/* Basic Information */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom color="primary" sx={{ display: 'flex', alignItems: 'center' }}>
                  <Info sx={{ mr: 1 }} /> Basic Information
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">Claim Number</Typography>
                  <Typography variant="body1" fontWeight="medium">{viewingClaim.claim_number || 'N/A'}</Typography>
                </Box>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">Status</Typography>
                  <Chip 
                    label={viewingClaim.status || 'Pending'} 
                    color={getStatusColor(viewingClaim.status)}
                    size="small"
                    sx={{ textTransform: 'capitalize' }}
                  />
                </Box>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">Policy ID</Typography>
                  <Typography variant="body1" fontWeight="medium">#{viewingClaim.policy_id}</Typography>
                </Box>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">Claim Type</Typography>
                  <Chip 
                    label={(viewingClaim.claim_type || 'unknown').replace('_', ' ')} 
                    color={getClaimTypeColor(viewingClaim.claim_type)}
                    size="small"
                    sx={{ textTransform: 'capitalize' }}
                  />
                </Box>
              </Grid>

              {/* Incident Details */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom color="primary" sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                  <Warning sx={{ mr: 1 }} /> Incident Details
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">Incident Date</Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {viewingClaim.incident_date ? new Date(viewingClaim.incident_date).toLocaleDateString() : 'N/A'}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">Cause of Loss</Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {viewingClaim.cause_of_loss || 'N/A'}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">Estimated Loss</Typography>
                  <Typography variant="body1" fontWeight="medium">
                    ₹{viewingClaim.estimated_loss ? parseFloat(viewingClaim.estimated_loss).toFixed(2) : '0.00'}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">Affected Area</Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {viewingClaim.affected_area || '0'} acres
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">Description</Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {viewingClaim.description || 'No description provided'}
                  </Typography>
                </Box>
              </Grid>

              {/* Supporting Documents */}
              {viewingClaim.supporting_documents && (
                <>
                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom color="primary" sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                      <Description sx={{ mr: 1 }} /> Supporting Documents
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                  </Grid>

                  <Grid item xs={12}>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body1" fontWeight="medium">
                        {viewingClaim.supporting_documents}
                      </Typography>
                    </Box>
                  </Grid>
                </>
              )}

              {/* Financial Information */}
              {(viewingClaim.approved_amount || viewingClaim.payout_amount) && (
                <>
                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom color="primary" sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                      <AttachMoney sx={{ mr: 1 }} /> Financial Information
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                  </Grid>

                  {viewingClaim.approved_amount && (
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary">Approved Amount</Typography>
                        <Typography variant="body1" fontWeight="medium">
                          ₹{viewingClaim.approved_amount ? parseFloat(viewingClaim.approved_amount).toFixed(2) : '0.00'}
                        </Typography>
                      </Box>
                    </Grid>
                  )}

                  {viewingClaim.payout_amount && (
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary">Payout Amount</Typography>
                        <Typography variant="body1" fontWeight="medium">
                          ₹{viewingClaim.payout_amount ? parseFloat(viewingClaim.payout_amount).toFixed(2) : '0.00'}
                        </Typography>
                      </Box>
                    </Grid>
                  )}
                </>
              )}

              {/* System Information */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom color="primary" sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                  <Info sx={{ mr: 1 }} /> System Information
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">Submitted At</Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {viewingClaim.created_at ? new Date(viewingClaim.created_at).toLocaleString() : 'N/A'}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">Claim ID</Typography>
                  <Typography variant="body1" fontWeight="medium">#{viewingClaim.id}</Typography>
                </Box>
              </Grid>

              {/* DOC Progress at Time of Claim */}
              {viewingClaimPolicy && viewingClaimPolicy.policy_start_date && (
                <>
                  <Grid item xs={12}>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="h6" gutterBottom color="primary" sx={{ display: 'flex', alignItems: 'center' }}>
                      <Info sx={{ mr: 1 }} /> DOC at Time of Claim
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <DOCProgressIndicator
                      stockingDate={viewingClaimPolicy.policy_start_date}
                      targetDOC={(() => {
                        try {
                          if (viewingClaimPolicy.policy_start_date && viewingClaimPolicy.policy_end_date) {
                            const startDate = new Date(viewingClaimPolicy.policy_start_date);
                            const endDate = new Date(viewingClaimPolicy.policy_end_date);
                            if (!isNaN(startDate.getTime()) && !isNaN(endDate.getTime())) {
                              return Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));
                            }
                          }
                        } catch (e) {
                          console.warn('Error calculating DOC for claim:', e);
                        }
                        return 120; // Default fallback
                      })()}
                      variant="linear"
                      size="medium"
                      showDetails={true}
                      label="Days of Culture (DOC)"
                    />
                    {viewingClaim.claim_date && (
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                        Claim filed on: {new Date(viewingClaim.claim_date).toLocaleDateString()}
                      </Typography>
                    )}
                  </Grid>
                </>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, bgcolor: 'grey.50' }}>
          <Button 
            onClick={handleCloseDetailsDialog}
            variant="outlined"
            sx={{ minWidth: 100 }}
          >
            Close
          </Button>
          {viewingClaim?.status === 'pending' && (
            <Button 
              onClick={() => {
                handleCloseDetailsDialog();
                handleOpenDialog(viewingClaim);
              }}
              variant="contained"
              color="primary"
              sx={{ minWidth: 120 }}
            >
              Edit Claim
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ClaimsManagement;
